import { useCallback, useEffect, useState } from "react";
import { Dismiss20Regular } from "@fluentui/react-icons";
import { APP_CURRENCY, APP_LOCALE } from "../../../config/locale";
import {
  ApiError,
  createArticle,
  deleteArticle,
  getArticleCategories,
  getArticleClassifications,
  getArticles,
  updateArticle,
  type Article,
  type ArticleCategory,
  type ArticleClassification,
  type ArticlePayload,
} from "../../../catalogo/catalogoApi";

const formatCurrency = (value?: number | null): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat(APP_LOCALE, {
    style: "currency",
    currency: APP_CURRENCY,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value).replace(/\./g, " ");
};

const parseNumber = (value: string): number | null => {
  const normalized = value.trim().replace(/\s/g, "").replace(",", ".");
  if (!normalized) return null;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export function ArticlesTab() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [classifications, setClassifications] = useState<ArticleClassification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [acceptingId, setAcceptingId] = useState<number | null>(null);

  const [codigo, setCodigo] = useState("");
  const [nome, setNome] = useState("");
  const [unidade, setUnidade] = useState("");
  const [categoriaId, setCategoriaId] = useState("");
  const [classificacaoId, setClassificacaoId] = useState("");
  const [preco, setPreco] = useState("");

  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [draft, setDraft] = useState<ArticlePayload | null>(null);

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      const [articlesResult, categoriesResult, classificationsResult] = await Promise.all([
        getArticles(),
        getArticleCategories(),
        getArticleClassifications(),
      ]);
      setArticles(articlesResult);
      setCategories(categoriesResult);
      setClassifications(classificationsResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar os artigos.");
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const classificationById = useCallback(
    (id?: number | null): ArticleClassification | undefined =>
      id === null || id === undefined ? undefined : classifications.find((c) => c.id === id),
    [classifications],
  );

  const classificationByCode = useCallback(
    (code?: string | null): ArticleClassification | undefined =>
      code === null || code === undefined || code === ""
        ? undefined
        : classifications.find((c) => c.code === code),
    [classifications],
  );

  const resetForm = () => {
    setCodigo("");
    setNome("");
    setUnidade("");
    setCategoriaId("");
    setClassificacaoId("");
    setPreco("");
  };

  const handleAdd = async () => {
    if (isSaving) return;
    if (!codigo.trim()) {
      setError("O código é obrigatório.");
      return;
    }
    if (!nome.trim()) {
      setError("A designação é obrigatória.");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await createArticle({
        code: codigo.trim(),
        name: nome.trim(),
        unit: unidade.trim() !== "" ? unidade.trim() : null,
        articleCategoryId: categoriaId !== "" ? Number(categoriaId) : null,
        classificationId: classificacaoId !== "" ? Number(classificacaoId) : null,
        referencePrice: parseNumber(preco),
      });
      resetForm();
      // Re-fetch so the server-computed suggestion appears for the new article.
      await loadData(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível adicionar o artigo.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (articleId: number) => {
    if (deletingId !== null) return;
    if (!window.confirm("Eliminar este artigo?")) return;
    setDeletingId(articleId);
    setError(null);
    try {
      await deleteArticle(articleId);
      setArticles((prev) => prev.filter((item) => item.id !== articleId));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setArticles((prev) => prev.filter((item) => item.id !== articleId));
      } else {
        setError(err instanceof Error ? err.message : "Não foi possível eliminar o artigo.");
        loadData(true);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const openEdit = (article: Article) => {
    setEditingArticle(article);
    setDraft({
      code: article.code ?? "",
      name: article.name ?? "",
      unit: article.unit ?? "",
      articleCategoryId: article.articleCategoryId ?? null,
      classificationId: article.classificationId ?? null,
      referencePrice: article.referencePrice ?? null,
    });
  };

  const handleAcceptSuggestion = async (article: Article) => {
    if (acceptingId !== null) return;
    const suggested = classificationByCode(article.suggestedClassificationCode);
    if (!suggested) return;
    setAcceptingId(article.id);
    setError(null);
    try {
      await updateArticle(article.id, {
        code: article.code,
        name: article.name,
        unit: article.unit ?? null,
        articleCategoryId: article.articleCategoryId ?? null,
        classificationId: suggested.id,
        referencePrice: article.referencePrice ?? null,
      });
      // Re-fetch so the server-computed suggestion/badge state is accurate.
      await loadData(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível aceitar a sugestão.");
    } finally {
      setAcceptingId(null);
    }
  };

  const saveEdit = async () => {
    if (!editingArticle || !draft) return;
    if (!draft.code.trim()) {
      setError("O código é obrigatório.");
      return;
    }
    if (!draft.name.trim()) {
      setError("A designação é obrigatória.");
      return;
    }
    setError(null);
    try {
      await updateArticle(editingArticle.id, {
        code: draft.code.trim(),
        name: draft.name.trim(),
        unit: draft.unit && draft.unit.trim() !== "" ? draft.unit.trim() : null,
        articleCategoryId: draft.articleCategoryId ?? null,
        classificationId: draft.classificationId ?? null,
        referencePrice: draft.referencePrice ?? null,
      });
      setEditingArticle(null);
      setDraft(null);
      // Re-fetch so the server-computed suggestion reflects the new name/category.
      await loadData(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível atualizar o artigo.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border p-5" style={{ borderRadius: "var(--radius-card)" }}>
        <h3 className="text-foreground mb-4" style={{ fontSize: "var(--text-section-title)", fontWeight: "var(--font-weight-semi-bold)" }}>Adicionar Artigo</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 md:items-end">
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Código *
            <input value={codigo} onChange={(event) => setCodigo(event.target.value)} maxLength={50} placeholder="Código" className="px-3 py-2 border border-border rounded bg-background text-foreground" />
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text xl:col-span-2">
            Designação *
            <input value={nome} onChange={(event) => setNome(event.target.value)} maxLength={200} placeholder="Designação" className="px-3 py-2 border border-border rounded bg-background text-foreground" />
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Unidade
            <input value={unidade} onChange={(event) => setUnidade(event.target.value)} maxLength={20} placeholder="m2, un, kg" className="px-3 py-2 border border-border rounded bg-background text-foreground" />
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Categoria
            <select value={categoriaId} onChange={(event) => setCategoriaId(event.target.value)} className="px-3 py-2 border border-border rounded bg-background text-foreground">
              <option value="">Sem categoria</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Classificação
            <select value={classificacaoId} onChange={(event) => setClassificacaoId(event.target.value)} className="px-3 py-2 border border-border rounded bg-background text-foreground">
              <option value="">—</option>
              {classifications.map((classification) => (
                <option key={classification.id} value={classification.id}>{classification.name}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Preço de referência (€)
            <input type="number" min="0" step="0.01" value={preco} onChange={(event) => setPreco(event.target.value)} placeholder="0" className="px-3 py-2 border border-border rounded bg-background text-foreground" />
          </label>
        </div>
        <div className="flex justify-end mt-4">
          <button
            type="button"
            disabled={isSaving}
            onClick={handleAdd}
            className="inline-flex items-center justify-center px-4 py-2 bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ borderRadius: "var(--radius)" }}
          >
            {isSaving ? "A adicionar…" : "Adicionar"}
          </button>
        </div>
      </div>

      {error && <div className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3" style={{ borderRadius: "var(--radius)" }}>{error}</div>}

      <div className="bg-card border border-border overflow-x-auto" style={{ borderRadius: "var(--radius-card)" }}>
        <table className="min-w-[760px] w-full">
          <thead>
            <tr className="border-b border-border bg-muted text-left">
              <th className="px-4 py-3 w-[140px]">Código</th>
              <th className="px-4 py-3">Designação</th>
              <th className="px-4 py-3 w-[100px]">Unidade</th>
              <th className="px-4 py-3 w-[180px]">Categoria</th>
              <th className="px-4 py-3 w-[180px]">Classificação</th>
              <th className="px-4 py-3 text-right w-[160px]">Preço Ref.</th>
              <th className="px-4 py-3 w-[140px]">Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-secondary-text">A carregar…</td></tr>
            ) : articles.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-secondary-text">Sem artigos.</td></tr>
            ) : articles.map((article) => {
              const confirmed = classificationById(article.classificationId);
              const suggested = !confirmed
                ? classificationByCode(article.suggestedClassificationCode)
                : undefined;
              return (
              <tr key={article.id} className="border-b border-border last:border-b-0 align-top">
                <td className="px-4 py-3 break-words">{article.code || "—"}</td>
                <td className="px-4 py-3 whitespace-normal break-words">{article.name || "—"}</td>
                <td className="px-4 py-3 break-words">{article.unit || "—"}</td>
                <td className="px-4 py-3 break-words">{article.categoryName || "—"}</td>
                <td className="px-4 py-3 break-words">
                  {confirmed ? (
                    <span
                      className="inline-flex items-center px-2 py-0.5 text-xs bg-primary/10 text-primary border border-primary/20"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      {confirmed.name}
                    </span>
                  ) : suggested ? (
                    <span className="inline-flex items-center gap-2 flex-wrap">
                      <span className="text-secondary-text italic text-xs">Sugerido: {suggested.name}</span>
                      <button
                        type="button"
                        className="text-primary underline text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={acceptingId === article.id}
                        onClick={() => handleAcceptSuggestion(article)}
                      >
                        {acceptingId === article.id ? "A aceitar…" : "Aceitar"}
                      </button>
                    </span>
                  ) : (
                    <span className="text-secondary-text">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right font-medium whitespace-nowrap">{formatCurrency(article.referencePrice)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2 flex-wrap">
                    <button className="text-primary underline" type="button" onClick={() => openEdit(article)}>Editar</button>
                    <button className="text-destructive underline disabled:opacity-50 disabled:cursor-not-allowed" type="button" disabled={deletingId === article.id} onClick={() => handleDelete(article.id)}>{deletingId === article.id ? "A eliminar…" : "Eliminar"}</button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editingArticle && draft && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-card border border-border p-6 w-full max-w-2xl space-y-4" style={{ borderRadius: "var(--radius-card)" }}>
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-foreground" style={{ fontSize: "var(--text-section-title)", fontWeight: "var(--font-weight-semi-bold)" }}>Editar artigo</h3>
              <button type="button" onClick={() => { setEditingArticle(null); setDraft(null); }} aria-label="Fechar" className="p-1 rounded hover:bg-accent/30 text-secondary-text">
                <Dismiss20Regular className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Código *
                <input placeholder="Código" value={draft.code ?? ""} maxLength={50} onChange={(e) => setDraft({ ...draft, code: e.target.value })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Unidade
                <input placeholder="m2, un, kg" value={draft.unit ?? ""} maxLength={20} onChange={(e) => setDraft({ ...draft, unit: e.target.value })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
                Designação *
                <input placeholder="Designação" value={draft.name ?? ""} maxLength={200} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Categoria
                <select value={draft.articleCategoryId ?? ""} onChange={(e) => setDraft({ ...draft, articleCategoryId: e.target.value !== "" ? Number(e.target.value) : null })} className="px-3 py-2 border border-border rounded bg-background text-foreground">
                  <option value="">Sem categoria</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Classificação
                <select value={draft.classificationId ?? ""} onChange={(e) => setDraft({ ...draft, classificationId: e.target.value !== "" ? Number(e.target.value) : null })} className="px-3 py-2 border border-border rounded bg-background text-foreground">
                  <option value="">—</option>
                  {classifications.map((classification) => (
                    <option key={classification.id} value={classification.id}>{classification.name}</option>
                  ))}
                </select>
                {(() => {
                  if (draft.classificationId) return null;
                  const suggested = classificationByCode(editingArticle.suggestedClassificationCode);
                  if (!suggested) return null;
                  return (
                    <span className="text-secondary-text italic text-xs">
                      Sugestão (por regras): {suggested.name}
                      <button
                        type="button"
                        className="text-primary underline ml-1"
                        onClick={() => setDraft((prev) => (prev ? { ...prev, classificationId: suggested.id } : prev))}
                      >
                        usar
                      </button>
                    </span>
                  );
                })()}
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Preço de referência (€)
                <input placeholder="0" value={draft.referencePrice ?? ""} onChange={(e) => setDraft({ ...draft, referencePrice: parseNumber(e.target.value) })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setEditingArticle(null); setDraft(null); }} className="px-4 py-2 border border-border">Cancelar</button>
              <button type="button" onClick={saveEdit} className="px-4 py-2 bg-primary text-primary-foreground">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
